import { useTranslation } from "react-i18next";
import { forwardRef, useEffect, useId, useRef, useState } from "react";
import { DataNotFound } from "../common/DataNotFound";
import type { MutableRefObject, ReactNode } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import clsx from "clsx";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** A small mark before the label, in the list and on the chosen value. */
  icon?: ReactNode;
}

interface SelectProps {
  /** Omit when the caller already renders its own <label> element. */
  label?: string;
  placeholder?: string;
  required?: boolean;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  disabled?: boolean;
  isLoading?: boolean;
  onBlur?: () => void;
  /** Shows a client-side search box above the list. Defaults to on once there are enough options to matter. */
  searchable?: boolean;
  className?: string;
  triggerClassName?: string;
  id?: string;
  name?: string;
  /** Bump this (e.g. a counter) to force the dropdown closed from outside —
   * for a trigger like an adjacent "+ Add new" button that opens a modal,
   * which isn't itself inside this component's outside-click boundary and
   * so wouldn't otherwise reliably close an already-open panel first. */
  closeSignal?: number;
  /**
   * Server-side search: called (debounced) with the search box's text so the
   * caller can fetch matching options, instead of loading every record up
   * front for the box to filter. Options are then shown as given — the
   * server may match on more than the label. Called with "" when the search
   * is cleared.
   */
  onSearchChange?: (query: string) => void;
}

/**
 * Custom-styled single-select dropdown — a drop-in replacement for a native
 * <select>, which renders with the OS/browser's own popup chrome (dark on
 * some platforms, no theming) instead of matching the app's design system.
 * Same interaction chrome as CreatableSelect, minus the "add new" affordance.
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  function Select(
    {
      label,
      placeholder,
      required = false,
      options,
      value,
      onChange,
      error,
      disabled = false,
      isLoading = false,
      onBlur,
      searchable,
      className,
      triggerClassName,
      id,
      name,
      closeSignal,
      onSearchChange,
    },
    ref,
  ) {
    const { t } = useTranslation("common");
    // Ties the visible label and error text to the trigger for assistive tech.
    const autoId = useId();
    const labelId = `${autoId}-label`;
    const errorId = `${autoId}-error`;
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    // Last query handed to onSearchChange, so opening/closing the panel
    // doesn't re-fetch the same list.
    const lastSearchSent = useRef("");
    const onSearchChangeRef = useRef(onSearchChange);
    useEffect(() => {
      onSearchChangeRef.current = onSearchChange;
    }, [onSearchChange]);
    useEffect(() => {
      if (!onSearchChangeRef.current) return;
      const query = search.trim();
      if (query === lastSearchSent.current) return;
      const timer = setTimeout(() => {
        lastSearchSent.current = query;
        onSearchChangeRef.current?.(query);
      }, 300);
      return () => clearTimeout(timer);
    }, [search]);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [panelStyle, setPanelStyle] = useState<{
      top?: number;
      bottom?: number;
      left: number;
      width: number;
    } | null>(null);

    const setTriggerRefs = (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref)
        (ref as MutableRefObject<HTMLButtonElement | null>).current = node;
    };

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (
          containerRef.current &&
          !containerRef.current.contains(target) &&
          !(panelRef.current && panelRef.current.contains(target))
        ) {
          setIsOpen(false);
          setSearch("");
          onBlur?.();
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [onBlur]);

    useEffect(() => {
      if (closeSignal === undefined) return;
      setIsOpen(false);
      setSearch("");
    }, [closeSignal]);

    // The dropdown panel is portaled to <body> so it can't be clipped by an
    // ancestor's overflow:hidden (e.g. ModalShell's rounded card, or a table
    // wrapper's overflow-x-auto, which implicitly clips the Y axis too) —
    // position is computed in viewport coordinates instead of relying on CSS
    // absolute positioning against a clipped parent.
    useEffect(() => {
      if (!isOpen || !triggerRef.current) return;

      const updatePosition = () => {
        const rect = triggerRef.current!.getBoundingClientRect();
        // Cap on the panel's own max-h-64 below — used only to decide whether
        // there's enough room, not to compute where the flipped panel sits.
        const estimatedHeight = 260;
        const spaceBelow = window.innerHeight - rect.bottom;
        if (spaceBelow < estimatedHeight && rect.top > spaceBelow) {
          // Not enough room below — anchor the panel's BOTTOM edge to the
          // trigger's top via CSS `bottom`, so it grows upward from there
          // regardless of how tall it actually renders (a handful of options
          // is much shorter than the 260px worst-case estimate above, and
          // computing `top` from that estimate left a visible gap between
          // the panel and its trigger).
          setPanelStyle({
            bottom: window.innerHeight - rect.top + 4,
            left: rect.left,
            width: rect.width,
          });
        } else {
          setPanelStyle({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
          });
        }
      };

      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen) return;

      const handleCloseAll = () => {
        setIsOpen(false);
        setSearch("");
      };

      window.addEventListener("close-all-selects", handleCloseAll);

      return () => {
        window.removeEventListener("close-all-selects", handleCloseAll);
      };
    }, [isOpen]);

    const showSearch = searchable ?? options.length > 8;
    const filteredOptions = showSearch && !onSearchChange
      ? options.filter((opt) =>
          opt.label.toLowerCase().includes(search.trim().toLowerCase()),
        )
      : options;

    const selected = options.find((opt) => opt.value === value);
    const listId = `${autoId}-list`;
    const optionId = (i: number) => `${autoId}-opt-${i}`;
    // The option the arrow keys are on; -1 when none.
    const [active, setActive] = useState(-1);
    const shown = Math.min(active, filteredOptions.length - 1);

    const close = (refocus = false) => {
      setIsOpen(false);
      setSearch("");
      setActive(-1);
      if (refocus) triggerRef.current?.focus();
    };

    const handleSelect = (opt: SelectOption) => {
      if (opt.disabled) return;
      onChange(opt.value);
      close(true);
    };

    const open = () => {
      if (disabled) return;
      setIsOpen(true);
      // Start on the chosen option, else the first one that can be picked.
      const at = filteredOptions.findIndex((o) => o.value === value);
      setActive(at >= 0 ? at : filteredOptions.findIndex((o) => !o.disabled));
    };

    const toggleOpen = () => {
      if (isOpen) close();
      else open();
    };

    /** The next option that can be picked, from `from` in direction `step` (wrapping). */
    const nextEnabled = (from: number, step: 1 | -1) => {
      const n = filteredOptions.length;
      for (let k = 1; k <= n; k++) {
        const i = (((from + step * k) % n) + n) % n;
        if (!filteredOptions[i].disabled) return i;
      }
      return -1;
    };

    // Keyboard, as a native select: arrows move, Enter picks, Escape closes the list only.
    const onKeyDown = (e: React.KeyboardEvent) => {
      if (disabled) return;
      if (!isOpen) {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          open();
        }
        return;
      }
      if (e.key === "Escape") {
        // Handled here, so a dialog around the select stays open (ModalShell skips a prevented Escape).
        e.preventDefault();
        e.stopPropagation();
        close(true);
      } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => nextEnabled(Math.min(i, filteredOptions.length - 1), e.key === "ArrowDown" ? 1 : -1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const opt = filteredOptions[shown];
        if (opt) handleSelect(opt);
      } else if (e.key === "Tab") {
        close();
      }
    };

    // Keep the highlighted option in view as the arrows move through a long list.
    useEffect(() => {
      if (isOpen && shown >= 0) document.getElementById(optionId(shown))?.scrollIntoView?.({ block: "nearest" });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, shown]);

    return (
      <div className={clsx("relative w-full", className)} ref={containerRef}>
        {label && (
          <label id={labelId} className="block text-xs font-semibold text-[rgb(var(--text-primary))] mb-1.5">
            {label} {required && <span className="text-red-600">*</span>}
          </label>
        )}

        <button
          ref={setTriggerRefs}
          type="button"
          id={id}
          name={name}
          disabled={disabled}
          onClick={toggleOpen}
          onKeyDown={onKeyDown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listId : undefined}
          aria-activedescendant={isOpen && shown >= 0 ? optionId(shown) : undefined}
          aria-labelledby={label ? labelId : undefined}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={clsx(
            "w-full flex items-center justify-between px-3 py-2.5 bg-[rgb(var(--card))] border rounded-lg text-xs transition-colors text-left",
            error ? "border-red-500" : "border-[rgb(var(--border))]",
            disabled
              ? "opacity-60 cursor-not-allowed"
              : "cursor-pointer hover:bg-[rgb(var(--card-muted))]",
            triggerClassName,
          )}
        >
          <span
            className={clsx(
              "truncate",
              selected
                ? "text-[rgb(var(--text-primary))]"
                : "text-[rgb(var(--text-muted))]",
            )}
          >
            {selected?.icon ? (
              <span className="flex min-w-0 items-center gap-2">
                <span className="shrink-0 text-[rgb(var(--text-accent))]">{selected.icon}</span>
                <span className="truncate">{selected.label}</span>
              </span>
            ) : (
              selected?.label || placeholder || t("select.select")
            )}
          </span>
          <div className="flex items-center gap-1.5 ml-2 shrink-0">
            {isLoading && (
              <Loader2
                size={14}
                className="animate-spin text-[rgb(var(--text-muted))]"
              />
            )}
            <ChevronDown
              size={14}
              className={clsx(
                "text-[rgb(var(--text-muted))] transition-transform duration-200",
                isOpen && "rotate-180",
              )}
            />
          </div>
        </button>

        {isOpen &&
          !disabled &&
          panelStyle &&
          createPortal(
            <div
              ref={panelRef}
              style={{
                position: "fixed",
                ...(panelStyle.top !== undefined
                  ? { top: panelStyle.top }
                  : {}),
                ...(panelStyle.bottom !== undefined
                  ? { bottom: panelStyle.bottom }
                  : {}),
                left: panelStyle.left,
                width: panelStyle.width,
              }}
              className="z-[10000] bg-[rgb(var(--card))] border border-[rgb(var(--border))] rounded-lg shadow-xl ring-1 ring-black/5 max-h-64 overflow-hidden flex flex-col"
            >
              {showSearch && (
                <div className="p-2 border-b border-[rgb(var(--border))] bg-[rgb(var(--card))]">
                  <input
                    type="text"
                    autoFocus
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setActive(0);
                    }}
                    onKeyDown={onKeyDown}
                    role="combobox"
                    aria-expanded
                    aria-controls={listId}
                    aria-activedescendant={shown >= 0 ? optionId(shown) : undefined}
                    placeholder={t("select.search")}
                    className="w-full px-2.5 py-1.5 bg-[rgb(var(--card))] border border-[rgb(var(--border))] rounded-lg text-xs text-[rgb(var(--text-primary))] focus:outline-none focus:border-[rgb(var(--text-accent))]"
                  />
                </div>
              )}

              <div id={listId} role="listbox" aria-labelledby={label ? labelId : undefined} className="overflow-y-auto max-h-56 p-1">
                {isLoading && options.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-[rgb(var(--text-muted))] text-center flex items-center justify-center gap-2">
                    <Loader2 size={14} className="animate-spin" />
                    <span>{t("select.loading")}</span>
                  </div>
                ) : filteredOptions.length > 0 ? (
                  filteredOptions.map((opt, index) => (
                    <div
                      key={opt.value}
                      id={optionId(index)}
                      role="option"
                      aria-selected={value === opt.value}
                      aria-disabled={opt.disabled || undefined}
                      onClick={() => handleSelect(opt)}
                      onMouseEnter={() => !opt.disabled && setActive(index)}
                      className={clsx(
                        "px-3 py-2.5 text-xs rounded-lg flex items-center justify-between gap-2 transition-colors",
                        index === shown && !opt.disabled && "bg-[rgb(var(--card-muted))] ring-1 ring-inset ring-[rgb(var(--text-accent)/0.4)]",
                        index > 0 &&
                          "mt-0.5 border-t border-[rgb(var(--border))]/60 pt-[calc(0.625rem+1px)]",
                        opt.disabled
                          ? "opacity-40 cursor-not-allowed"
                          : "cursor-pointer hover:bg-[rgb(var(--card-muted))]",
                        value === opt.value &&
                          "bg-[rgb(var(--card-muted))] font-semibold",
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-2 text-[rgb(var(--text-primary))]">
                        {opt.icon && (
                          <span className="shrink-0 text-[rgb(var(--text-accent))]">
                            {opt.icon}
                          </span>
                        )}
                        <span className="truncate">{opt.label}</span>
                      </span>
                      {value === opt.value && (
                        <Check
                          size={13}
                          className="text-[rgb(var(--text-accent))] shrink-0"
                        />
                      )}
                    </div>
                  ))
                ) : (
                  <DataNotFound inline title={t("select.noOptions")} />
                )}
              </div>
            </div>,
            document.body,
          )}

        {error && (
          <p id={errorId} role="alert" className="text-xs text-red-600 mt-1">
            {error}
          </p>
        )}
      </div>
    );
  },
);
